import React from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  Icon,
} from "@chakra-ui/react";
import {
  LockIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";

const DocumentRequestCard = ({
  request,
  onClick,
}) => {
  // ============================================================
  // STATUS
  // ============================================================

  const status = (
    request?.status || "PENDING"
  ).toUpperCase();

  const statusConfig = {
    PENDING: {
      label: "Pending",
      color: "#F59E0B",
      bg: "#FFF1DD",
      iconBg: "#F59E0B",
    },

    PROCESSING: {
      label: "Processing",
      color: "#3B82F6",
      bg: "#E8F1FF",
      iconBg: "#3B82F6",
    },

    INITIATED: {
      label: "Initiated",
      color: "#3B82F6",
      bg: "#E8F1FF",
      iconBg: "#3B82F6",
    },

    COMPLETED: {
      label: "Completed",
      color: "#22C55E",
      bg: "#E4F8EA",
      iconBg: "#22C55E",
    },

    REVIEW: {
      label: "Review",
      color: "#A855F7",
      bg: "#F3E8FF",
      iconBg: "#A855F7",
    },

    REOPEN: {
      label: "Reopen",
      color: "#EAB308",
      bg: "#FEF9C3",
      iconBg: "#EAB308",
    },

    FAILED: {
      label: "Failed",
      color: "#EF4444",
      bg: "#FEE2E2",
      iconBg: "#EF4444",
    },

    REJECTED: {
      label: "Rejected",
      color: "#EF4444",
      bg: "#FEE2E2",
      iconBg: "#EF4444",
    },

    CLOSED: {
      label: "Closed",
      color: "#6B7280",
      bg: "#F3F4F6",
      iconBg: "#9CA3AF",
    },
  };

  const currentStatus =
    statusConfig[status] ||
    statusConfig.PENDING;


  // ============================================================
  // DATE
  // ============================================================

  const requestDate = request?.created_at
    ? new Date(request.created_at)
    : null;

  const formattedDate =
    requestDate && !isNaN(requestDate.getTime())
      ? requestDate.toLocaleDateString("en-GB")
      : "-";


  // ============================================================
  // CATEGORY
  // ============================================================

  const category =
    request?.category ||
    request?.category_name ||
    request?.service_category ||
    request?.categoryName ||
    "Docs";


  // ============================================================
  // REQUEST NUMBER
  // ============================================================

  const requestNumber =
    request?.service_request_no ||
    request?.request_no ||
    request?.request_number ||
    `REQ-${request?.id || ""}`;


  // ============================================================
  // SERVICE NAME
  // ============================================================

  const serviceName =
    request?.service_type_name ||
    request?.serviceName ||
    request?.service_type ||
    "Document Request";


  // ============================================================
  // CLICK
  // ============================================================

  const handleClick = () => {
    if (onClick) {
      onClick(request);
    }
  };


  // ============================================================
  // RENDER
  // ============================================================

  return (
    <Box
      width="100%"
      bg="white"
      borderRadius="24px"
      px={4}
      py={4}
      mb={3}
      cursor="pointer"

      border="1.5px solid"
      borderColor={
        status === "COMPLETED"
          ? "#35C46A"
          : "transparent"
      }

      boxShadow="0 2px 10px rgba(0, 0, 0, 0.06)"

      onClick={handleClick}

      transition="all 0.2s ease"

      _hover={{
        transform: "translateY(-1px)",
        boxShadow:
          "0 4px 14px rgba(0, 0, 0, 0.10)",
      }}
    >

      {/* ========================================================
          TOP ROW
      ======================================================== */}

      <Flex
        align="center"
        justify="space-between"
        width="100%"
      >

        {/* LEFT */}

        <Flex
          align="center"
          minW={0}
          flex="1"
        >

          {/* STATUS ICON */}

          <Flex
            width="56px"
            height="56px"
            minW="56px"
            borderRadius="50%"
            bg={currentStatus.iconBg}
            align="center"
            justify="center"
            mr={4}
          >
            <Icon
              as={LockIcon}
              color="white"
              boxSize={5}
            />
          </Flex>


          {/* REQUEST NUMBER */}

          <Text
            fontSize="22px"
            fontWeight="700"
            color="gray.900"
            noOfLines={1}
          >
            {requestNumber}
          </Text>

        </Flex>


        {/* RIGHT */}

        <Flex
          align="center"
          ml={2}
          flexShrink={0}
        >

          {/* STATUS */}

          <Badge
            px={5}
            py={2.5}
            borderRadius="30px"
            bg={currentStatus.bg}
            color={currentStatus.color}
            fontSize="16px"
            fontWeight="700"
            textTransform="none"
            whiteSpace="nowrap"
          >
            {currentStatus.label}
          </Badge>


          {/* CHEVRON */}

          <ChevronRightIcon
            boxSize={8}
            color="gray.500"
            ml={2}
          />

        </Flex>

      </Flex>


      {/* ========================================================
          SERVICE NAME
      ======================================================== */}

      <Text
        fontSize="20px"
        fontWeight="700"
        color="gray.900"
        mt={2}
        noOfLines={1}
      >
        {serviceName}
      </Text>


      {/* ========================================================
          CATEGORY + DATE
      ======================================================== */}

      <Flex
        width="100%"
        align="center"
        justify="space-between"
        mt={2}
      >

        {/* CATEGORY */}

        <Text
          fontSize="16px"
          color="gray.700"
          fontWeight="400"
        >
          {category}
        </Text>


        {/* DATE - RIGHT SIDE */}

        <Text
          fontSize="16px"
          color="gray.700"
          fontWeight="400"
          whiteSpace="nowrap"
        >
          {formattedDate}
        </Text>

      </Flex>

    </Box>
  );
};

export default DocumentRequestCard;